import * as Web3 from 'web3';
import { BigNumber } from '../utils/bignumber';
import { Web3Utils } from '../utils/web3_utils';

import { ContractsAPI, SignerAPI } from '../apis';
import {
  ApplicationOrderData,
  APPLICATION_ORDER_DATA_DEFAULTS,
  QuoteOrderData,
  QUOTE_ORDER_DATA_DEFAULTS,
  PolicyOrderData,
  TxData,
  ECDSASignature,
  PolicyRegistryEntry,
} from '../types';
import { generatePseudoRandomSalt } from '../utils/salt';
import { generateTxOptions } from '../utils/transaction_utils';

const ORDER_FILL_GAS_MAXIMUM = 600000

export class OrdersAPI {
    private web3: Web3;
    private sign: SignerAPI;
    private contracts: ContractsAPI;

    public constructor(web3: Web3, contracts: ContractsAPI, sign: SignerAPI) {
        this.web3 = web3;
        this.sign = sign;
        this.contracts = contracts;
    }

    public async createApplicationOrder(
        responses: any[],
        duration?: BigNumber,
        desiredCoverage?: BigNumber
    ): Promise<ApplicationOrderData> {
        let salt = generatePseudoRandomSalt();
        let applicationHash = this.generateApplicationHash(responses, salt);

        let appOrderData: ApplicationOrderData = {
            applicationHash,
            salt,
            responses,
            type: APPLICATION_ORDER_DATA_DEFAULTS.type,
            duration: duration ? duration : APPLICATION_ORDER_DATA_DEFAULTS.duration,
            desiredCoverage: desiredCoverage ? desiredCoverage : APPLICATION_ORDER_DATA_DEFAULTS.desiredCoverage,
            expiration: APPLICATION_ORDER_DATA_DEFAULTS.expiration
        }

        return appOrderData;
    }

    public async createQuoteOrder(
        applicationHash: string,
        carrier: string,
        premium: BigNumber,
        coverageLimit: BigNumber,
        withPrefix: boolean,
        duration?: BigNumber
    ): Promise<QuoteOrderData> {
        let defaultExpiration = QUOTE_ORDER_DATA_DEFAULTS.expiration;

        let quoteHash = this.generateQuoteHash(applicationHash, coverageLimit, premium, defaultExpiration);

        const signature: ECDSASignature = await this.sign.signPayloadWithAddress(
            quoteHash,
            carrier,
            withPrefix
        )

        let quoteOrderData: QuoteOrderData = {
            applicationHash,
            premium,
            coverageLimit,
            carrier,
            type: QUOTE_ORDER_DATA_DEFAULTS.type,
            duration: duration ? duration : QUOTE_ORDER_DATA_DEFAULTS.duration,
            expiration: defaultExpiration,
            carrierSignature: signature
        }

        return quoteOrderData;
    }

    public async createPolicyOrder(quoteOrderData: QuoteOrderData): Promise<PolicyOrderData> {
        let quoteHash = this.generateQuoteHash(
            quoteOrderData.applicationHash,
            quoteOrderData.coverageLimit,
            quoteOrderData.premium,
            quoteOrderData.expiration
        );

        let policyOrderData: PolicyOrderData = {
            applicationHash: quoteOrderData.applicationHash,
            quoteHash,
            premium: quoteOrderData.premium,
            coverageLimit: quoteOrderData.coverageLimit,
            carrier: quoteOrderData.carrier,
            expiration: quoteOrderData.expiration,
            carrierSignature: quoteOrderData.carrierSignature
        }

        return policyOrderData;
    }

  public async fillPolicyOrderAsync(
    quoteOrderData: QuoteOrderData,
    options?: TxData
  ): Promise<string> {
    const { policyRegistry } = await this.contracts.loadAsekuroContractsAsync(options);

    const txOptions = await generateTxOptions(this.web3, ORDER_FILL_GAS_MAXIMUM, options);

    let policyOrderData = await this.createPolicyOrder(quoteOrderData);

    // TODO perform some check to make sure data is valid/complete

    return policyRegistry.fillPolicyOrder.sendTransactionAsync(
      policyOrderData.carrier,
      policyOrderData.applicationHash,
      policyOrderData.quoteHash,
      policyOrderData.coverageLimit,
      policyOrderData.premium,
      policyOrderData.expiration,
      policyOrderData.carrierSignature.v,
      policyOrderData.carrierSignature.r,
      policyOrderData.carrierSignature.s,
      txOptions
    )
  }

    public async getPolicy(applicationHash: string): Promise<PolicyRegistryEntry> {
        const { policyRegistry } = await this.contracts.loadAsekuroContractsAsync();
        return policyRegistry.getPolicyEntry.callAsync(applicationHash)
    }

    public generateApplicationHash(responseValues: any[], salt: BigNumber): string {
        return Web3Utils.soliditySHA3(...responseValues, salt)
    }

    public generateQuoteHash(
        applicationHash: string,
        coverageLimit: BigNumber,
        premium: BigNumber,
        expiration: BigNumber
    ): string {
        return Web3Utils.soliditySHA3(applicationHash, coverageLimit, premium, expiration)
    }
}
